import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Heading,
  HStack,
  Pressable,
  Stack,
  Text,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { GoogleLogin } from "react-google-login";
import { FaFacebookF } from "react-icons/fa";
import styles from "./styles.module.css";
import bus from "../../assets/images/bus.png";
import logo from "../../assets/images/logo_simple.png";
import colors from "../../constants/colors";
import constants from "../../constants";
import { StateContext, StatusContext } from "../../contexts";
import API from "../../api";
import routes from "../../routes";
import google from "../../assets/icons/google.svg";
import Image from "../../components/image";

const disableSocialLogin = true;

function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { role } = useParams();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const action = useMemo(() => searchParams.get("action"), [searchParams]);
  const url = useMemo(() => searchParams.get("url"), [searchParams]);

  const onLogin = async () => {
    try {
      setIsLoading(true);
      const {
        data: { jwt },
      } = await API().post("/auth/local", {
        identifier: email,
        password,
      });
      const user = await state.refreshUser(jwt);
      localStorage.setItem(constants.state.JWT, jwt);
      state.updateJwt(jwt);
      state.updateIsLoggedIn(true);
      if (action === "quotation" && url && url.match(/^.+\/(\d+)\/.+$/)) {
        const quotationId = url.match(/^.+\/(\d+)\/.+$/)[1];
        try {
          await API(state.jwt).put(`/quotations/${quotationId}`, {
            data: {
              user: user.id,
              agency: user?.agency?.id || null,
            },
          });
          if (user.isVendor) {
            navigate(
              routes.agencyExperienceCreationPurchaseSummary.replace(
                ":id",
                quotationId,
              ),
            );
          } else {
            navigate(
              routes.experienceCreationPurchaseSummary.replace(
                ":id",
                quotationId,
              ),
            );
          }
          return;
        } catch (error) {
          status.updateErrorMessage(error);
        }
      }
      if (user.isVendor) {
        if (!user.agency) {
          navigate(routes.signUpAgencyRnt);
        } else if (user.missingAgencyData) {
          navigate(routes.signUpAgencyData);
        } else if (url) {
          navigate(url);
        } else {
          navigate(routes.agency);
        }
      } else if (url) {
        navigate(url);
      } else {
        navigate(routes.quotations);
      }
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsLoading(false);
  };

  const onFacebookSignUp = async () => {
    try {
      setIsLoading(true);
      if (window.FB) {
        const response = await new Promise((resolve) =>
          window.FB.login(resolve, { scope: "public_profile,email" }),
        );
        if (response.authResponse) {
          alert(
            `https://graph.facebook.com/me?fields=id,email,name,first_name,last_name&access_token=${response.authResponse.accessToken}`,
          );
          setIsLoading(false);
        } else {
          throw new Error("Error iniciando sesión, por favor intente de nuevo");
        }
      } else {
        throw new Error("Opción no disponible, por favor intente de nuevo");
      }
    } catch (error) {
      setIsLoading(false);
      status.updateErrorMessage(error);
    }
  };

  const onGoogleSignUp = async (user) => {
    try {
      alert(`https://oauth2.googleapis.com/tokeninfo?id_token=${user.tokenId}`);
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsLoading(false);
  };

  return (
    <HStack flex={1} alignItems="stretch">
      <Helmet>
        <title>Inicio de sesión</title>
      </Helmet>
      <VStack flex={4} overflowY="auto" padding={8} space={10}>
        <Link to={routes.home}>
          <Image image={logo} alt="logo" width={150} height={80} />
        </Link>
        <Heading color={colors.secondary} fontSize="2xl">
          Iniciar sesión
        </Heading>
        <VStack width="70%" space={5}>
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              onLogin();
            }}>
            <label className={styles.input}>
              <Text color={colors.secondary}>Correo electrónico</Text>
              <input
                placeholder="Ingresa tu correo electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </label>
            <label className={styles.input}>
              <Text color={colors.secondary}>Contraseña</Text>
              <input
                placeholder="Ingresa tu contraseña"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </label>
            <button disabled={isLoading}>
              <Box
                backgroundColor={colors.primary}
                padding={2}
                borderRadius="full"
                width="full">
                <Text color={colors.white}>Iniciar sesión</Text>
              </Box>
            </button>
          </form>
          <HStack width="full" space={5}>
            <Box flex={1} backgroundColor={colors.lightGray} height="1px" />
            <Text>O</Text>
            <Box flex={1} backgroundColor={colors.lightGray} height="1px" />
          </HStack>
          {role !== "agency" && !disableSocialLogin && (
            <Pressable
              backgroundColor={colors.facebook}
              color={colors.white}
              borderRadius={30}
              onPress={onFacebookSignUp}
              disabled={isLoading}>
              <HStack justifyContent="center" minH={45} space={2}>
                <FaFacebookF size={20} />
                <Text bold color={colors.white}>
                  Facebook
                </Text>
              </HStack>
            </Pressable>
          )}
          {role !== "agency" && !disableSocialLogin && (
            <GoogleLogin
              clientId="430546055165-bjagl3hjkgjc6pu9gvim8n1n0reip04d.apps.googleusercontent.com"
              render={(renderProps) => (
                <Pressable
                  backgroundColor={colors.white}
                  borderRadius={30}
                  borderWidth={2}
                  borderColor={colors.lightGray}
                  onPress={() => {
                    setIsLoading(true);
                    renderProps.onClick();
                  }}
                  disabled={renderProps.disabled || isLoading}>
                  <HStack justifyContent="center" minH={41} space={2}>
                    <Image image={google} alt="google" height={20} width={20} />
                    <Text bold>Google</Text>
                  </HStack>
                </Pressable>
              )}
              onSuccess={onGoogleSignUp}
              cookiePolicy="single_host_origin"
            />
          )}
          <Link to={role === "agency" ? routes.signUpVendor : routes.signUp}>
            <center>
              <u>¿No tienes cuenta? Regístrate</u>
            </center>
          </Link>
        </VStack>
      </VStack>
      <VStack flex={6}>
        <Stack flex={1} overflow="hidden">
          <Image
            image={bus}
            alt="bus"
            width="100%"
            height="100%"
            objectFit="cover"
            flex={1}
          />
        </Stack>
      </VStack>
    </HStack>
  );
}

export default SignInPage;
